import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, MagnifyingGlassIcon, TagIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button, Input, Card, CardContent, CardHeader, CardTitle, Badge } from '../../components/ui';
import { licenseTypeService } from '../../services/licenseTypeService';
import LicenseTypeModal from '../../components/LicenseTypeModal';
import { SUCCESS_MESSAGES } from '../../constants';
import {
  isApiRequestError,
  drfErrorsToFormState,
  buildFormErrorBanner,
  formatApiErrorForUser,
} from '../../utils/apiErrors';

const LicenseTypesPage = () => {
  console.log('LicenseTypesPage rendering - Final Version');
  
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLicenseType, setEditingLicenseType] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [licenseTypeModalBanner, setLicenseTypeModalBanner] = useState('');
  const [licenseTypeModalFieldErrors, setLicenseTypeModalFieldErrors] = useState({});
  const [deleteError, setDeleteError] = useState('');
  const queryClient = useQueryClient();
  
  // Fetch license types from API
  const { data: licenseTypesData, isLoading, error } = useQuery({
    queryKey: ['licenseTypes', searchTerm],
    queryFn: () => licenseTypeService.getLicenseTypes({ search: searchTerm }),
  });
  
  console.log('API Response:', { licenseTypesData, isLoading, error });
  
  // Use API data or fallback to empty array
  const licenseTypes = licenseTypesData?.results || licenseTypesData || [];
  
  // Create license type mutation
  const createLicenseTypeMutation = useMutation({
    mutationFn: (data) => licenseTypeService.createLicenseType(data),
    onMutate: () => {
      setLicenseTypeModalBanner('');
      setLicenseTypeModalFieldErrors({});
    },
    onSuccess: (response) => {
      console.log('License type created successfully:', response);
      queryClient.invalidateQueries(['licenseTypes']);
      setIsModalOpen(false);
      // You can add a success notification here
    },
    onError: (error) => {
      console.error('Error creating license type:', error);
      if (isApiRequestError(error)) {
        const { fieldErrors } = drfErrorsToFormState(error.body?.errors);
        setLicenseTypeModalFieldErrors(fieldErrors);
        setLicenseTypeModalBanner(buildFormErrorBanner(error));
      } else {
        setLicenseTypeModalBanner(error?.message || 'No se pudo crear el tipo de licencia');
      }
    }
  });
  
  const handleCreateLicenseType = (data) => {
    createLicenseTypeMutation.mutate(data);
  };

  // Update license type mutation
  const updateLicenseTypeMutation = useMutation({
    mutationFn: ({ id, data }) => licenseTypeService.updateLicenseType(id, data),
    onMutate: () => {
      setLicenseTypeModalBanner('');
      setLicenseTypeModalFieldErrors({});
    },
    onSuccess: (response) => {
      console.log('License type updated successfully:', response);
      queryClient.invalidateQueries(['licenseTypes']);
      setEditingLicenseType(null);
      setIsModalOpen(false);
    },
    onError: (error) => {
      console.error('Error updating license type:', error);
      if (isApiRequestError(error)) {
        const { fieldErrors } = drfErrorsToFormState(error.body?.errors);
        setLicenseTypeModalFieldErrors(fieldErrors);
        setLicenseTypeModalBanner(buildFormErrorBanner(error));
      } else {
        setLicenseTypeModalBanner(error?.message || 'No se pudo actualizar el tipo de licencia');
      }
    }
  });

  // Delete license type mutation
  const deleteLicenseTypeMutation = useMutation({
    mutationFn: (id) => licenseTypeService.deleteLicenseType(id),
    onSuccess: () => {
      console.log('License type deleted successfully');
      queryClient.invalidateQueries(['licenseTypes']);
      setDeleteConfirmId(null);
      setDeleteError('');
    },
    onError: (error) => {
      console.error('Error deleting license type:', error);
      setDeleteError(
        isApiRequestError(error) ? formatApiErrorForUser(error) : error?.message || 'No se pudo eliminar el tipo de licencia'
      );
    }
  });

  const handleEditLicenseType = (licenseType) => {
    clearLicenseTypeModalServerErrors();
    setEditingLicenseType(licenseType);
    setIsModalOpen(true);
  };

  const handleDeleteLicenseType = (id) => {
    setDeleteError('');
    setDeleteConfirmId(id);
  };

  const confirmDelete = () => {
    if (deleteConfirmId) {
      deleteLicenseTypeMutation.mutate(deleteConfirmId);
    }
  };

  const handleModalSubmit = (data) => {
    if (editingLicenseType) {
      updateLicenseTypeMutation.mutate({ id: editingLicenseType.id, data });
    } else {
      createLicenseTypeMutation.mutate(data);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingLicenseType(null);
    setLicenseTypeModalBanner('');
    setLicenseTypeModalFieldErrors({});
  };

  const clearLicenseTypeModalServerErrors = () => {
    setLicenseTypeModalBanner('');
    setLicenseTypeModalFieldErrors({});
  };
  
  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="text-center py-12">
        <h3 className="mt-2 text-sm font-medium text-gray-900">Error al cargar tipos de licencia</h3>
        <p className="mt-1 text-sm text-gray-500">{error.message}</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tipos de Licencia</h1>
          <p className="text-gray-600">Catálogo de nombres; precio y promos van en cada licencia</p>
        </div>
        <Button onClick={() => {
          setEditingLicenseType(null);
          clearLicenseTypeModalServerErrors();
          setIsModalOpen(true);
        }}>
          <PlusIcon className="h-4 w-4 mr-2" />
          Nuevo Tipo
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-6">
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              placeholder="Buscar tipos de licencia..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* License Types Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <TagIcon className="h-5 w-5 mr-2" />
            Lista de Tipos de Licencia
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo de Licencia
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Trial
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {licenseTypes.map((licenseType) => (
                  <tr key={licenseType.id} className="hover:bg-gray-50 transition-colors">
                    <td className="py-4 px-6">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{licenseType.name}</div>
                        {licenseType.description && (
                          <div className="text-sm text-gray-500 mt-1">{licenseType.description}</div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <Badge variant={licenseType.is_active ? 'success' : 'danger'}>
                        {licenseType.is_active ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </td>
                    <td className="py-4 px-6">
                      {licenseType.is_trial_type ? (
                        <Badge variant="warning" size="sm">
                          Sí
                        </Badge>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditLicenseType(licenseType)}
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="Editar tipo de licencia"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteLicenseType(licenseType.id)}
                          className="text-red-600 hover:text-red-800 transition-colors"
                          title="Eliminar tipo de licencia"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

         {/* Debug info */}
         <div className="bg-green-50 rounded-lg p-4">
           <p className="text-green-800 text-sm">✅ Módulo de Tipos de Licencia funcionando correctamente</p>
           <p className="text-green-800 text-sm">Término de búsqueda: "{searchTerm}"</p>
           <p className="text-green-800 text-sm">Total de tipos: {licenseTypes.length}</p>
           <p className="text-green-800 text-sm">Estado de carga: {isLoading ? 'Cargando...' : 'Completado'}</p>
           {error && <p className="text-red-800 text-sm">Error: {error.message}</p>}
         </div>

      {/* License Type Creation/Edit Modal */}
      <LicenseTypeModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        isLoading={createLicenseTypeMutation.isPending || updateLicenseTypeMutation.isPending}
        editingLicenseType={editingLicenseType}
        serverBanner={licenseTypeModalBanner}
        serverFieldErrors={licenseTypeModalFieldErrors}
        onDismissServerErrors={clearLicenseTypeModalServerErrors}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Confirmar Eliminación
              </h3>
              <p className="text-gray-600 mb-6">
                ¿Estás seguro de que deseas eliminar este tipo de licencia? Esta acción no se puede deshacer.
              </p>
              {deleteError && (
                <div
                  role="alert"
                  className="mb-4 rounded-md bg-red-50 border border-red-200 p-3 text-red-800 text-sm whitespace-pre-wrap"
                >
                  {deleteError}
                </div>
              )}
              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setDeleteConfirmId(null);
                    setDeleteError('');
                  }}
                  disabled={deleteLicenseTypeMutation.isPending}
                >
                  Cancelar
                </Button>
                <Button
                  variant="danger"
                  onClick={confirmDelete}
                  disabled={deleteLicenseTypeMutation.isPending}
                >
                  {deleteLicenseTypeMutation.isPending ? 'Eliminando...' : 'Eliminar'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LicenseTypesPage;
